"use client"

import type React from "react"
import { useState, useMemo, useEffect } from "react"
import {
  FaSearch,
  FaEdit,
  FaFileInvoice,
  FaMoneyBillWave,
  FaRupeeSign,
  FaFilter,
  FaFileExcel,
  FaBuilding,
  FaMapMarkerAlt,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa"
import type { Billing, BillingStatus, PoStatus, WccStatus } from "./index"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import { Pagination } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { formatDate } from "../activities/edit"
import axiosInstance from "../../libs/axios"
import { Link } from "react-router-dom"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiSettings,
  FiRefreshCw,
} from "react-icons/fi"
import { SearchableSelect } from "../../utils/select-with-search"
import type { SelectOption } from "../../utils/select-with-search"

export interface BillingFilters {
  search?: string
  billing_status?: string
  site_id?: string
  project_id?: string
  project_label?: string
  activity_id?: string
  activity_label?: string
  circle_name?: string
  state_name?: string
  wcc_status?: string
  invoice_status?: string
  wcc_number?: string
  invoice_number?: string
  po_number?: string
  receipt_number?: string
  activity_amount_min?: string
  activity_amount_max?: string
  invoice_amount_min?: string
  invoice_amount_max?: string
  created_from?: string
  created_to?: string
  po_date_from?: string
  po_date_to?: string
  wcc_date_from?: string
  wcc_date_to?: string
  invoice_date_from?: string
  invoice_date_to?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: BillingFilters
  onFiltersChange: (filters: BillingFilters) => void
  onApply: () => void
  onReset: () => void
  projectOptions: SelectOption[]
  activityOptions: SelectOption[]
}

const BillingFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  projectOptions,
  activityOptions,
}) => {
  const [activeTab, setActiveTab] = useState('basic')
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null)
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null)

  const projectSelectOptions = useMemo(
    () => projectOptions.map((project) => ({
      id: Number(project.value ?? project.id ?? project.label),
      value: project.value ?? project.id,
      label: project.label ?? "",
    })),
    [projectOptions],
  )

  const activitySelectOptions = useMemo(
    () => activityOptions.map((activity) => ({
      id: Number(activity.value ?? activity.id ?? activity.label),
      value: activity.value ?? activity.id,
      label: activity.label ?? "",
    })),
    [activityOptions],
  )

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'amounts', label: 'Amounts', icon: FaRupeeSign },
    { id: 'other', label: 'Other', icon: FiSettings },
  ]

  useEffect(() => {
    if (isOpen) {
      setActiveTab('basic')
    }
  }, [isOpen])

  useEffect(() => {
    if (filters.project_id) {
      const existing = projectSelectOptions.find((option) => String(option.value) === filters.project_id)
      setSelectedProject(
        existing ||
        (filters.project_label
          ? {
            id: Number(filters.project_id),
            value: filters.project_id,
            label: filters.project_label,
          }
          : null),
      )
    } else {
      setSelectedProject(null)
    }
  }, [filters.project_id, filters.project_label, projectSelectOptions])

  useEffect(() => {
    if (filters.activity_id) {
      const existing = activitySelectOptions.find((option) => String(option.value) === filters.activity_id)
      setSelectedActivity(
        existing ||
        (filters.activity_label
          ? {
            id: Number(filters.activity_id),
            value: filters.activity_id,
            label: filters.activity_label,
          }
          : null),
      )
    } else {
      setSelectedActivity(null)
    }
  }, [filters.activity_id, filters.activity_label, activitySelectOptions])

  if (!isOpen) return null

  const handleInputChange = (field: keyof BillingFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const handleProjectSelect = (option: SelectOption | null) => {
    setSelectedProject(option)
    onFiltersChange({
      ...filters,
      project_id: option ? String(option.value) : undefined,
      project_label: option ? option.label : undefined,
    })
  }

  const handleActivitySelect = (option: SelectOption | null) => {
    setSelectedActivity(option)
    onFiltersChange({
      ...filters,
      activity_id: option ? String(option.value) : undefined,
      activity_label: option ? option.label : undefined,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ref ID, PO, or Invoice Number"
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Site ID"
                  value={filters.site_id || ''}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                />
              </div>
              {/** Legacy project text filter commented per instructions */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Project ID"
                  value={filters.project_id || ''}
                  onChange={(e) => handleInputChange('project_id', e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                <SearchableSelect
                  value={selectedProject}
                  onChange={handleProjectSelect}
                  options={projectSelectOptions}
                  placeholder="Search project"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                <SearchableSelect
                  value={selectedActivity}
                  onChange={handleActivitySelect}
                  options={activitySelectOptions}
                  placeholder="Search activity"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.billing_status || ''}
                  onChange={(e) => handleInputChange('billing_status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="wcc_raised">WCC Raised</option>
                  <option value="wcc_approved">WCC Approved</option>
                  <option value="invoice_raised">Invoice Raised</option>
                  <option value="invoice_submitted">Invoice Submitted</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WCC Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.wcc_status || ''}
                  onChange={(e) => handleInputChange('wcc_status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.invoice_status || ''}
                  onChange={(e) => handleInputChange('invoice_status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Circle</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Circle name"
                  value={filters.circle_name || ''}
                  onChange={(e) => handleInputChange('circle_name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State name"
                  value={filters.state_name || ''}
                  onChange={(e) => handleInputChange('state_name', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.po_date_from || ''}
                  onChange={(e) => handleInputChange('po_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.po_date_to || ''}
                  onChange={(e) => handleInputChange('po_date_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WCC Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.wcc_date_from || ''}
                  onChange={(e) => handleInputChange('wcc_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WCC Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.wcc_date_to || ''}
                  onChange={(e) => handleInputChange('wcc_date_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.invoice_date_from || ''}
                  onChange={(e) => handleInputChange('invoice_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.invoice_date_to || ''}
                  onChange={(e) => handleInputChange('invoice_date_to', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === 'amounts' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Amount (Min)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="₹"
                  value={filters.activity_amount_min || ''}
                  onChange={(e) => handleInputChange('activity_amount_min', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Amount (Max)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="₹"
                  value={filters.activity_amount_max || ''}
                  onChange={(e) => handleInputChange('activity_amount_max', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Amount (Min)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="₹"
                  value={filters.invoice_amount_min || ''}
                  onChange={(e) => handleInputChange('invoice_amount_min', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Amount (Max)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="₹"
                  value={filters.invoice_amount_max || ''}
                  onChange={(e) => handleInputChange('invoice_amount_max', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Others Tab */}
          {activeTab === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="PO Number"
                  value={filters.po_number || ''}
                  onChange={(e) => handleInputChange('po_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WCC Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="WCC Number"
                  value={filters.wcc_number || ''}
                  onChange={(e) => handleInputChange('wcc_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Invoice Number"
                  value={filters.invoice_number || ''}
                  onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Receipt Number"
                  value={filters.receipt_number || ''}
                  onChange={(e) => handleInputChange('receipt_number', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply()
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface BillingsListProps {
  billings: Billing[]
  pagination: {
    offset: number
    limit: number
    page: number
    total_pages: number
    total_count: number
    has_next_page: boolean
    has_prev_page: boolean
    next_page: number | null
    prev_page: number | null
  }
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onProjectSiteFilter: (projectSiteId: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddBilling: () => void
  onEditBilling: (billing: Billing) => void
  onDeleteBilling: (billing: Billing) => void
  onUpdateBillingStatus: (billing: Billing, status: BillingStatus) => void
  onUpdatePoStatus: (billing: Billing, status: PoStatus) => void
  onUpdateWccStatus: (billing: Billing, status: WccStatus) => void
  onAdvancedFilter?: (filters: BillingFilters) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  projectOptions?: SelectOption[]
  activityOptions?: SelectOption[]
  appliedFilters?: BillingFilters
  searchValue?: string
  statusValue?: string
  projectSiteValue?: string
}

export const BillingsList: React.FC<BillingsListProps> = ({
  billings,
  pagination,
  onSearch,
  onStatusFilter,
  onProjectSiteFilter,
  onPageChange,
  onLimitChange,
  onAddBilling,
  onEditBilling,
  onDeleteBilling,
  onUpdateBillingStatus,
  onUpdatePoStatus,
  onUpdateWccStatus,
  onAdvancedFilter,
  loading = false,
  sortConfig,
  onSort,
  projectOptions = [],
  activityOptions = [],
  appliedFilters,
  searchValue = "",
  statusValue = "",
  projectSiteValue = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [projectSiteFilter, setProjectSiteFilter] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [filters, setFilters] = useState<BillingFilters>({})
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])

  const activeFilterEntries = useMemo(() => {
    const entries: { key: keyof BillingFilters; label: string; value: string }[] = []

    Object.entries(filters).forEach(([key, rawValue]) => {
      if (!rawValue) return
      if (key === "project_label" || key === "activity_label") return

      let label = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
      let value = String(rawValue)

      if (key === "project_id") {
        label = "Project"
        value = filters.project_label || value
      } else if (key === "activity_id") {
        label = "Activity"
        value = filters.activity_label || value
      }

      entries.push({ key: key as keyof BillingFilters, label, value })
    })

    return entries
  }, [filters])

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setStatusFilter(value)
    onStatusFilter(value)
  }

  const handleProjectSiteFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setProjectSiteFilter(value)
    onProjectSiteFilter(value)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
  }

  const getActiveFiltersCount = () => activeFilterEntries.length

  const handleRemoveFilter = (key: keyof BillingFilters) => {
    const newFilters = { ...filters }
    delete newFilters[key]

    if (key === "project_id") {
      delete newFilters.project_label
    }

    if (key === "activity_id") {
      delete newFilters.activity_label
    }

    setFilters(newFilters)
    onAdvancedFilter?.(newFilters)
  }

  const formatDate1 = (dateString: string | Date | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (!amount) return "₹0.00"
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(numAmount)
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction =
      sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  const getBillingStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string; bgClass: string; textClass: string }> = {
      pending: { color: "gray", label: "Pending", bgClass: "bg-gray-100", textClass: "text-gray-800" },
      wcc_raised: { color: "blue", label: "WCC Raised", bgClass: "bg-blue-100", textClass: "text-blue-800" },
      wcc_approved: { color: "indigo", label: "WCC Approved", bgClass: "bg-indigo-100", textClass: "text-indigo-800" },
      invoice_raised: {
        color: "purple",
        label: "Invoice Raised",
        bgClass: "bg-purple-100",
        textClass: "text-purple-800",
      },
      invoice_submitted: {
        color: "yellow",
        label: "Invoice Submitted",
        bgClass: "bg-yellow-100",
        textClass: "text-yellow-800",
      },
      paid: { color: "green", label: "Paid", bgClass: "bg-green-100", textClass: "text-green-800" },
    }

    const config = statusConfig[status] || statusConfig["pending"]
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgClass} ${config.textClass}`}
      >
        {config.label}
      </span>
    )
  }

  const getWccStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string; bgClass: string; textClass: string }> = {
      pending: { color: "gray", label: "Pending", bgClass: "bg-gray-100", textClass: "text-gray-800" },
      approved: { color: "green", label: "Approved", bgClass: "bg-green-100", textClass: "text-green-800" },
      rejected: { color: "red", label: "Rejected", bgClass: "bg-red-100", textClass: "text-red-800" },
    }

    const config = statusConfig[status] || statusConfig["pending"]
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgClass} ${config.textClass}`}
      >
        {config.label}
      </span>
    )
  }

  const getInvoiceStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string; bgClass: string; textClass: string }> = {
      draft: { color: "gray", label: "Draft", bgClass: "bg-gray-100", textClass: "text-gray-800" },
      submitted: { color: "blue", label: "Submitted", bgClass: "bg-blue-100", textClass: "text-blue-800" },
      approved: { color: "green", label: "Approved", bgClass: "bg-green-100", textClass: "text-green-800" },
      paid: { color: "green", label: "Paid", bgClass: "bg-green-100", textClass: "text-green-800" },
      cancelled: { color: "red", label: "Cancelled", bgClass: "bg-red-100", textClass: "text-red-800" },
    }

    const config = statusConfig[status] || statusConfig["draft"]
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgClass} ${config.textClass}`}
      >
        {config.label}
      </span>
    )
  }

  const getAppliedFilters = () => appliedFilters ?? {}

  const getSearchParam = () => (searchValue ?? "").trim() || searchTerm.trim()

  const getStatusParam = () => (statusValue ?? statusFilter ?? "").trim()

  const getProjectSiteParam = () => (projectSiteValue ?? projectSiteFilter ?? "").trim()

  const mapBillingForExport = (billing: Billing) => {
    const projectSite = billing.project_site
    const stateCircle = projectSite?.state_circle
    const project = projectSite?.project
    const activity = project?.activity

    const siteId = projectSite?.site_id || billing.manual_site_reference || ""
    const siteName = projectSite?.site_name || billing.manual_site_name || ""
    const projectName = project?.project_name || billing.manual_project_name || ""
    const activityName = activity?.activity_name || billing.manual_activity_name || ""
    const circleName = stateCircle?.circle_name || billing.manual_circle_name || ""
    const stateName = stateCircle?.state_name || ""

    const formatValue = (value?: string | null) => (value ? formatDate(value) : "")

    return {
      "Site ID": siteId,
      "Site Name": siteName,
      "Project Name": projectName,
      "Activity Name": activityName,
      Circle: circleName,
      State: stateName,
      "Billing Status": billing.billing_status,
      "Activity Amount": billing.activity_amount,
      "TC PO Number": billing.tc_po_number || "",
      "TC PO Date": formatValue(billing.tc_po_date),
      "PO Shared At Allocation": billing.is_po_given_at_allocation ? "Yes" : "No",
      "Billed Site ID": billing.billed_site_id || "",
      "Billed Ref ID": billing.billed_ref_id || "",
      "Billed PO Number": billing.billed_po_number || "",
      "Billed PO Date": formatValue(billing.billed_po_date),
      "WCC Number": billing.wcc_number || "",
      "WCC Date": formatValue(billing.wcc_date),
      "WCC Status": billing.wcc_status || "",
      "Receipt Number": billing.receipt_number || "",
      "Invoice Number": billing.invoice_number || "",
      "Invoice Date": formatValue(billing.invoice_date),
      "Invoice Amount": billing.invoice_amount || "",
      "Invoice Status": billing.invoice_status || "",
      "Manual Client Allocation Date": formatValue(billing.manual_client_allocation_date),
      "Manual Circle": billing.manual_circle_name || "",
      "Manual Project": billing.manual_project_name || "",
      "Manual Activity": billing.manual_activity_name || "",
      "Created At": formatDate(billing.created_at),
      "Updated At": formatDate(billing.updated_at),
    }
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const baseParams: Record<string, any> = {}

      const searchParam = getSearchParam()
      if (searchParam) baseParams.search = searchParam

      const statusParam = getStatusParam()
      if (statusParam) baseParams.billing_status = statusParam

      const projectSiteParam = getProjectSiteParam()
      if (projectSiteParam) baseParams.project_site_id = projectSiteParam

      const filtersParam = getAppliedFilters()
      Object.entries(filtersParam).forEach(([key, value]) => {
        if (!value) return
        if (key === "project_label" || key === "activity_label") return
        baseParams[key] = value
      })

      if (sortConfig?.field) {
        baseParams.sort_by = sortConfig.field
        baseParams.sort_order = sortConfig.direction
      }

      const allBillings: Billing[] = []
      let currentPage = 1
      let totalPages = 1
      const pageSize = Number(pagination?.limit) || 10

      do {
        const params = {
          ...baseParams,
          page: currentPage,
          limit: pageSize,
        }

        const response = await axiosInstance.get("/billing", { params })
        if (!response.data?.success) {
          break
        }

        const responseBillings: Billing[] = response.data.data?.billings || []
        allBillings.push(...responseBillings)

        const paginationInfo = response.data.data?.pagination || {}
        totalPages =
          paginationInfo.total_pages ??
          paginationInfo.totalPages ??
          paginationInfo.total_pages ??
          paginationInfo.total_pages ??
          currentPage

        currentPage += 1
      } while (currentPage <= totalPages)

      if (allBillings.length) {
        setExportData(allBillings.map(mapBillingForExport))
      } else {
        setExportData([])
      }
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting billing data:", error)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FaFileInvoice className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Billing Management</h2>
              <p className="text-sm text-gray-600">Manage invoices, POs, and WCCs</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
            {/* <button
              onClick={onAddBilling}
              className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaPlus className="w-4 h-4" />
              Add New Billing
            </button> */}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-center mb-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by ref ID, PO, invoice..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          {/* <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Billing Status</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="wcc_raised">WCC Raised</option>
                <option value="wcc_approved">WCC Approved</option>
                <option value="invoice_raised">Invoice Raised</option>
                <option value="invoice_submitted">Invoice Submitted</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div> */}

          {/* Project Site Filter */}
          {/* <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Site ID / Name</label>
            <div className="relative">
              <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Filter by site ID or name"
                value={projectSiteFilter}
                onChange={handleProjectSiteFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div> */}

          {/* Advanced Filter Button */}
          <div className="w-full sm:w-auto">
            <label className="block text-xs font-medium text-gray-700 mb-1">Filters</label>

            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Active Filters:</span>
              {activeFilterEntries.map(({ key, label, value }) => (
                <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {label}: {value}
                  <button
                    onClick={() => handleRemoveFilter(key)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button
                onClick={handleResetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      <BillingFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        projectOptions={projectOptions}
        activityOptions={activityOptions}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("billed_ref_id")}
                >
                  <span>Ref ID</span>
                  {getSortIcon("billed_ref_id")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("site_id")}
                >
                  <span>Site ID</span>
                  {getSortIcon("site_id")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Site Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Project Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Circle</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">State</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("billing_status")}
                >
                  <span>Billing Status</span>
                  {getSortIcon("billing_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("activity_amount")}
                >
                  <span>Activity Amount</span>
                  {getSortIcon("activity_amount")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("billed_po_number")}
                >
                  <span>PO Number</span>
                  {getSortIcon("billed_po_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("billed_po_date")}
                >
                  <span>PO Date</span>
                  {getSortIcon("billed_po_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("wcc_number")}
                >
                  <span>WCC Number</span>
                  {getSortIcon("wcc_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("wcc_date")}
                >
                  <span>WCC Date</span>
                  {getSortIcon("wcc_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("wcc_status")}
                >
                  <span>WCC Status</span>
                  {getSortIcon("wcc_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("invoice_number")}
                >
                  <span>Invoice Number</span>
                  {getSortIcon("invoice_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("invoice_date")}
                >
                  <span>Invoice Date</span>
                  {getSortIcon("invoice_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("invoice_amount")}
                >
                  <span>Invoice Amount</span>
                  {getSortIcon("invoice_amount")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("invoice_status")}
                >
                  <span>Invoice Status</span>
                  {getSortIcon("invoice_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleColumnSort("created_at")}
                >
                  <span>Created At</span>
                  {getSortIcon("created_at")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={20} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading billings...</span>
                  </div>
                </td>
              </tr>
            ) : billings.length === 0 ? (
              <tr>
                <td colSpan={20} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <FaFileInvoice className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-500">No billings found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new billing</p>
                  </div>
                </td>
              </tr>
            ) : (
              billings.map((billing) => (
                <tr key={billing.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEditBilling(billing)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                        title="Edit billing"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/billings/${billing.id}`}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                        title="View billing"
                      >
                        <FaEye className="w-4 h-4" />
                      </Link>
                      {/* <button
                        onClick={() => onDeleteBilling(billing)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                        title="Delete billing"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button> */}
                    </div>
                  </td>

                  {/* Ref ID */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{billing.billed_ref_id || "N/A"}</div>
                  </td>

                  {/* Site ID */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FaBuilding className="w-3 h-3 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {billing.project_site?.site_id || "N/A"}
                      </span>
                    </div>
                  </td>

                  {/* Site Name */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{billing.project_site?.site_name || "N/A"}</div>
                  </td>

                  {/* Project Name */}
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {billing.project_site?.project?.project_name || "N/A"}
                    </div>
                  </td>

                  {/* Activity */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {billing.project_site?.project?.activity?.activity_name || "N/A"}
                    </div>
                  </td>

                  {/* Circle */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      <FaMapMarkerAlt className="w-3 h-3 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {billing.project_site?.state_circle?.circle_name || "N/A"}
                      </span>
                    </div>
                  </td>

                  {/* State */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {billing.project_site?.state_circle?.state_name || "N/A"}
                    </div>
                  </td>

                  {/* Billing Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getBillingStatusBadge(billing.billing_status)}</td>

                  {/* Activity Amount */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FaMoneyBillWave className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(billing.activity_amount)}
                      </span>
                    </div>
                  </td>

                  {/* PO Number */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{billing.billed_po_number || "N/A"}</div>
                  </td>

                  {/* PO Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{formatDate1(billing.billed_po_date)}</div>
                  </td>

                  {/* WCC Number */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{billing.wcc_number || "N/A"}</div>
                  </td>

                  {/* WCC Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{formatDate1(billing.wcc_date)}</div>
                  </td>

                  {/* WCC Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getWccStatusBadge(billing.wcc_status)}</td>

                  {/* Invoice Number */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{billing.invoice_number || "N/A"}</div>
                  </td>

                  {/* Invoice Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{formatDate1(billing.invoice_date)}</div>
                  </td>

                  {/* Invoice Amount */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 font-medium">{formatCurrency(billing.invoice_amount)}</span>
                  </td>

                  {/* Invoice Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getInvoiceStatusBadge(billing.invoice_status)}</td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{formatDate(billing.created_at)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="billing"
        title="Export Billing Data"
        description="Download the filtered billing records with full details"
      />
    </div>
  )
}
